import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import TheField from "@/components/TheField";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";

// A one-time, cinematic Luminous Day welcome shown on a member's first Home load.
export default function WelcomeOverlay({ name, userId }) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!userId) return;
    const key = `mirro_welcomed_${userId}`;
    if (!localStorage.getItem(key)) {
      const t = setTimeout(() => setShow(true), 350);
      return () => clearTimeout(t);
    }
  }, [userId]);

  const dismiss = () => {
    try { localStorage.setItem(`mirro_welcomed_${userId}`, "1"); } catch {}
    setShow(false);
  };

  const first = (name || "").split(" ")[0] || "there";
  const lines = ["Everything here is built around one idea:", "you don't need to become more impressive —", "just more fully yourself."];

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.6 }}
          onClick={dismiss}
          className="fixed inset-0 z-[100] flex items-center justify-center px-6 overflow-hidden bg-background"
          data-testid="welcome-overlay"
        >
          <button onClick={dismiss} data-testid="welcome-close" aria-label="Skip welcome"
            className="absolute top-5 right-5 z-10 flex items-center gap-1 px-3 py-1.5 rounded-full text-xs text-muted-foreground hover:text-foreground border border-white/10">
            Skip
          </button>
          {/* drifting ambient glows */}
          <motion.div className="pointer-events-none absolute -top-32 left-1/4 w-[560px] h-[560px] rounded-full"
            style={{ background: "radial-gradient(circle, hsl(195 100% 55% / 0.4) 0%, transparent 62%)", filter: "blur(70px)" }}
            animate={{ x: [0, 40, 0], y: [0, 30, 0] }} transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }} />
          <motion.div className="pointer-events-none absolute bottom-0 right-1/4 w-[480px] h-[480px] rounded-full"
            style={{ background: "radial-gradient(circle, hsl(12 90% 62% / 0.85) 0%, transparent 60%)", filter: "blur(80px)" }}
            animate={{ x: [0, -30, 0], y: [0, -20, 0] }} transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }} />

          <div className="relative flex flex-col items-center text-center max-w-lg">
            <motion.div initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 1.1, ease: "easeOut" }} className="mb-8">
              <TheField intensity={0.9} size={220} active />
            </motion.div>

            <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
              className="text-xs uppercase tracking-[0.28em] text-primary mb-4">Welcome to Xcelio</motion.p>

            <motion.h1 initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.7 }}
              className="font-serif text-4xl sm:text-5xl tracking-tight mb-6" data-testid="welcome-name">
              Hello, {first}.
            </motion.h1>

            <div className="mb-10 space-y-1">
              {lines.map((l, i) => (
                <motion.p key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.0 + i * 0.35, duration: 0.6 }}
                  className={`text-base sm:text-lg ${i === 2 ? "text-foreground font-medium" : "text-muted-foreground"}`}>
                  {l}
                </motion.p>
              ))}
            </div>

            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 2.1, duration: 0.6 }}>
              <Button size="lg" onClick={dismiss} data-testid="welcome-begin-btn"
                className="rounded-full group px-8 py-6 text-base bg-gradient-to-r from-blue-500 to-teal-400 hover:opacity-95 shadow-lg shadow-blue-500/20">
                Begin <ArrowRight className="w-4 h-4 ml-1.5 transition-transform duration-300 group-hover:translate-x-1" />
              </Button>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
